import React from 'react'

import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Card } from '@amzn/stencil-react-components/card'
import { Col, Row } from '@amzn/stencil-react-components/layout'
import { Link } from '@amzn/stencil-react-components/link'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { Spinner } from '@amzn/stencil-react-components/spinner'
import { H2, Text } from '@amzn/stencil-react-components/text'

import { ItemContainerMetadataDTO } from 'src/models/dto/item-pools/ItemPoolMetadataDTO'
import { pluralize, ROW_BORDER } from 'src/pages/import-content/ImportContentModel'

export interface CreateStepProps {
    isSubmitting: boolean
    containers?: ItemContainerMetadataDTO[]
    hasError: boolean
    onBack: () => void
    onDone: () => void
}

export const CreateStep = ({
    isSubmitting,
    containers,
    hasError,
    onBack,
    onDone,
}: CreateStepProps) => (
    <Card isElevated width='100%' dataTestId='import-content-create-step'>
        <Col gridGap='S300' width='100%'>
            <H2 fontSize='T300'>Create your item container modules</H2>

            {isSubmitting && (
                <Row alignItems='center' gridGap='S300'>
                    <Spinner dataTestId='import-content-spinner' />
                    <Text color='neutral70'>Importing your spreadsheet…</Text>
                </Row>
            )}

            {containers &&
                (containers.length === 0 ? (
                    <Text dataTestId='import-no-containers'>
                        No Item Container Modules were generated.
                    </Text>
                ) : (
                    <>
                        <MessageBanner
                            type={MessageBannerType.Success}
                            isDismissible={false}
                            dataTestId='import-success'
                        >
                            {`Created ${pluralize(containers.length, 'Item Container module')}.`}
                        </MessageBanner>
                        <Col gridGap='S100' dataTestId='generated-item-containers'>
                            {containers.map((container) => (
                                <Row
                                    key={container.versionId}
                                    alignItems='center'
                                    justifyContent='space-between'
                                    gridGap='S200'
                                    padding={{ top: 'S200', bottom: 'S200' }}
                                    style={ROW_BORDER}
                                >
                                    <Col gridGap='S100'>
                                        <Link
                                            href={`/module/viewer/${container.versionId}`}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            {container.name}
                                        </Link>
                                        {!!container.locales?.length && (
                                            <Text fontSize='T100' color='neutral70'>
                                                Locales: {container.locales.join(', ')}
                                            </Text>
                                        )}
                                    </Col>
                                    <Button
                                        variant={ButtonVariant.Tertiary}
                                        size={ButtonSize.Small}
                                        dataTestId={`copy-${container.versionId}`}
                                        onClick={() =>
                                            navigator.clipboard?.writeText(container.versionId)
                                        }
                                    >
                                        Copy id
                                    </Button>
                                </Row>
                            ))}
                        </Col>
                    </>
                ))}

            <Row gridGap='S300'>
                {hasError && (
                    <Button
                        dataTestId='import-content-back-to-review'
                        variant={ButtonVariant.Tertiary}
                        onClick={onBack}
                    >
                        Back to review
                    </Button>
                )}
                {containers && (
                    <Button
                        dataTestId='import-content-done'
                        variant={ButtonVariant.Primary}
                        onClick={onDone}
                    >
                        Back to module builder
                    </Button>
                )}
            </Row>
        </Col>
    </Card>
)
